'use client'

import { Button } from '@/core/components/form/button/button'
import { useEffect, useState } from 'react'

import { publicApiHost } from '@/core/config/env'
import { cn } from '@/core/utils/common'

import { api } from '@/features/veluvit/services/api'
import { refreshSession } from '@/features/veluvit/services/session.service'
import {
  beginSocialLogin,
  finishSocialLogin,
} from '@/features/veluvit/services/social-login'
import { closeAll } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { ReactNode } from 'react'

type ProviderId = 'kakao' | 'google' | 'apple'
type Provider = {
  id: ProviderId
  name: string
  enabled: boolean
  authorizeUrl: string
}

/* 공급자 표시 — 색·로고는 각 사 로그인 버튼 가이드(카카오 노랑 #FEE500, 구글 흰 바탕 4색 G, Apple 검정)를 따른다.
   지원 범위는 카카오·Google·Apple + 이메일로 정했다(2026-09-29). 서버가 네이버·X 를 켜도 여기 없으면 그리지 않는다. 순서는 국내 이용 빈도순 */
const BRAND: Record<
  ProviderId,
  { label: string; logo: ReactNode; className: string }
> = {
  kakao: {
    label: '카카오',
    className: 'bg-[#fee500] text-black/85',
    logo: (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="size-5"
      >
        <path
          fill="currentColor"
          d="M12 3C6.48 3 2 6.52 2 10.86c0 2.8 1.87 5.26 4.68 6.65l-.95 3.5c-.08.3.26.54.52.37l4.17-2.77c.52.06 1.05.1 1.58.1 5.52 0 10-3.52 10-7.85S17.52 3 12 3Z"
        />
      </svg>
    ),
  },
  google: {
    label: 'Google',
    className: 'bg-white text-[#1f1f1f]',
    logo: (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="size-5"
      >
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.94l3.66-2.84Z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
        />
      </svg>
    ),
  },
  apple: {
    label: 'Apple',
    // Google 이 흰 바탕이라 Apple 은 가이드의 검정판 — 어두운 모달 위에서 테두리로 윤곽을 준다
    className: 'border border-foreground/20 bg-black text-white',
    logo: (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className="size-5"
      >
        <path
          fill="currentColor"
          d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 14.35 3.85 5.31 9.39 5.03c1.34.07 2.28.76 3.07.82 1.18-.24 2.3-.95 3.56-.86 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.55 4.36l-.3.33ZM12.03 4.97c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"
        />
      </svg>
    ),
  },
}
const ORDER: ProviderId[] = ['kakao', 'google', 'apple']

/** 서버가 준 이동 주소는 이 사이트(프록시)나 백엔드 도메인의 소셜 시작 경로만 믿는다 — 다른 곳으로 보내는 주소면 버튼을 만들지 않는다 */
function safeAuthorizeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  try {
    const url = new URL(value, window.location.origin)
    const hosts = [window.location.host, publicApiHost].filter(Boolean)
    const secure = url.protocol === 'https:' || url.hostname === 'localhost'
    return secure &&
      hosts.includes(url.host) &&
      url.pathname.startsWith('/api/user/social/')
      ? url.href
      : null
  } catch {
    return null
  }
}

/** 한 번 받은 공급자 목록 — 로그인 모달을 다시 열 때 자리표시 없이 바로 그려 높이가 덜컥 바뀌지 않게 */
let cached: Provider[] | null = null

async function loadProviders(): Promise<Provider[]> {
  const data = await api<{ providers?: Partial<Provider>[] }>('/user/social/')
  return (
    (data.providers || [])
      .flatMap((p) => {
        const id = p.id as ProviderId
        const authorizeUrl = safeAuthorizeUrl(p.authorizeUrl)
        return ORDER.includes(id) && p.enabled && authorizeUrl
          ? [
              {
                id,
                name: p.name || BRAND[id].label,
                enabled: true,
                authorizeUrl,
              },
            ]
          : []
      })
      // 서버 순서가 아니라 우리 순서(카카오 먼저)로 세운다
      .sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id))
  )
}

/* 누르면 이동 중인 버튼만 옅게, 나머지는 막는다. 포커스는 ring 대신 밝기로 */
const PRESS =
  'cursor-pointer transition-[filter,opacity] duration-150 hover:brightness-95 focus-visible:brightness-90 disabled:cursor-not-allowed disabled:opacity-60'

/**
 * 간편 로그인 — 서버에 키가 설정된(enabled) 공급자만 보인다. 누르면 그 공급자의 동의 화면으로 페이지를 옮긴다(fetch 아님).
 * 로그인 뒤 서버가 정해 둔 주소로 돌아오고, 앱이 다시 뜨면서 초기 데이터·CSRF 를 새로 받는다.
 * 목록을 못 받거나 켜진 공급자가 없으면 아무것도 그리지 않는다 — 이메일로 시작하면 된다.
 */
export default function SocialLogin({ className }: { className?: string }) {
  const [providers, setProviders] = useState<Provider[] | null>(cached)
  const [leaving, setLeaving] = useState<ProviderId | null>(null)

  useEffect(() => {
    let alive = true
    if (cached) return
    loadProviders()
      .then((list) => {
        cached = list
        if (alive) setProviders(list)
      })
      .catch(() => alive && setProviders([]))
    return () => {
      alive = false
    }
  }, [])

  // 동의 화면에서 뒤로가기로 돌아오면 브라우저가 떠나던 순간의 페이지를 그대로 꺼낸다(bfcache) — ‘이동하는 중…’ · 막힌 버튼을 되돌린다
  useEffect(() => {
    const reset = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      setLeaving(null)
      // 동의 화면에서 로그인까지 마치고 뒤로 돌아왔을 수도 있다 — 세션을 다시 읽어 로그인됐으면 결과를 알리고 창을 닫는다
      void refreshSession()
        .then(() => {
          const user = useSessionStore.getState().data?.user ?? null
          finishSocialLogin(user)
          if (user && !user.guest) closeAll()
        })
        .catch(() => {})
    }
    window.addEventListener('pageshow', reset)
    return () => window.removeEventListener('pageshow', reset)
  }, [])

  // 목록을 받는 동안 버튼 자리와 '또는 이메일로' 줄 자리까지 잡아 둔다 — 도착하면서 모달 높이가 덜컥 바뀌지 않게
  if (!providers) {
    return (
      <div aria-hidden="true" className={className}>
        <div className="flex flex-col gap-2">
          <span className="h-12 animate-pulse rounded-full bg-foreground/[0.05]" />
          <span className="h-12 animate-pulse rounded-full bg-foreground/[0.05]" />
        </div>
        <div className="mt-6 h-[1.1rem]" />
      </div>
    )
  }
  if (!providers.length) return null

  const go = (p: Provider) => {
    setLeaving(p.id)
    beginSocialLogin(p.id, p.authorizeUrl)
  }
  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        {providers.map((p) => (
          <Button
            key={p.id}
            type="button"
            disabled={!!leaving}
            aria-busy={leaving === p.id}
            onClick={() => go(p)}
            className={cn(
              'relative flex h-12 w-full items-center justify-center rounded-full px-12 text-c15 font-semibold',
              PRESS,
              BRAND[p.id].className
            )}
            variant="bare"
          >
            <span className="absolute left-5 grid size-6 place-items-center">
              {BRAND[p.id].logo}
            </span>
            {leaving === p.id
              ? '이동하는 중…'
              : `${BRAND[p.id].label}로 계속하기`}
          </Button>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-3 text-c12 text-t3 before:h-px before:flex-1 before:bg-line2 before:content-[''] after:h-px after:flex-1 after:bg-line2 after:content-['']">
        또는 이메일로
      </div>
    </div>
  )
}
